import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import getApiBase from "../utils/apiBase";
import { useI18n } from "../i18n/I18nContext";
import "./AdminOrders.css";

const API_BASE = getApiBase();
const SHIPMENT_STATUSES = ["preparing", "shipped", "delivered"];
const ORDER_STATUSES = ["paid", "pending", "unpaid", "canceled", "cancelled", "refunded", "failed"];

const requestErrorKey = (status, fallback) => {
  if (status === 401 || status === 403) return "admin.orders.accessDenied";
  if (status === 404) return "admin.orders.notFound";
  if (status === 429) return "admin.orders.rateLimited";
  if (status === 400) return "admin.orders.invalidShipment";
  return fallback;
};

const itemImage = (item) => (
  item.previewImageUrl
  || item.imageUrl
  || item.customColorImageUrl
  || (Array.isArray(item.images) ? item.images[0] : "")
);

const emptyShipment = {
  shipmentStatus: "preparing",
  trackingCarrier: "Yanwen",
  trackingNumber: "",
  trackingUrl: "",
  adminShippingNote: "",
};

const AdminOrders = () => {
  const { t, formatDate, formatNumber, formatRegion } = useI18n();
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [shipmentDraft, setShipmentDraft] = useState(emptyShipment);
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const orderDate = (value) => value ? formatDate(value, { dateStyle: "medium", timeStyle: "short" }) : t("admin.notSet");
  const shipmentLabel = (value) => t(`admin.orders.shipment.${SHIPMENT_STATUSES.includes(value) ? value : "preparing"}`);
  const orderStatusLabel = (value) => t(`admin.orders.payment.${ORDER_STATUSES.includes(value) ? value : "unknown"}`);

  const authHeaders = useMemo(
    () => (auth ? { Authorization: `Basic ${auth}` } : {}),
    [auth],
  );

  const selectedOrder = useMemo(
    () => orders.find((item) => item.id === selectedId) || orders[0] || null,
    [orders, selectedId],
  );

  const fetchOrders = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setStatusMessage("");
    try {
      const query = search.trim() ? `?q=${encodeURIComponent(search.trim())}` : "";
      const response = await fetch(`${API_BASE}/api/admin/orders${query}`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        setStatusMessage(requestErrorKey(response.status, "admin.orders.loadFailed"));
        return;
      }
      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items : [];
      setOrders(items);
      setSelectedId((current) => current || items[0]?.id || "");
    } catch (error) {
      setStatusMessage("admin.orders.loadFailed");
    } finally {
      setLoading(false);
    }
  }, [auth, authHeaders, search]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!selectedOrder) return;
    setShipmentDraft({
      shipmentStatus: selectedOrder.shipmentStatus || "preparing",
      trackingCarrier: selectedOrder.trackingCarrier || "Yanwen",
      trackingNumber: selectedOrder.trackingNumber || "",
      trackingUrl: selectedOrder.trackingUrl || "",
      adminShippingNote: selectedOrder.adminShippingNote || "",
    });
  }, [selectedOrder]);

  const handleLogin = (event) => {
    event.preventDefault();
    const token = btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", token);
    setAuth(token);
    setStatusMessage("admin.loggedIn");
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuth("");
    setLogin({ username: "", password: "" });
    setOrders([]);
    setSelectedId("");
    setStatusMessage("admin.loggedOut");
  };

  const saveShipment = async () => {
    if (!selectedOrder) return;
    setSaving(true);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/orders/${selectedOrder.id}/shipment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(shipmentDraft),
      });
      if (!response.ok) {
        setStatusMessage(requestErrorKey(response.status, "admin.orders.saveFailed"));
        return;
      }
      const data = await response.json();
      const updated = data.order;
      setOrders((current) => current.map((item) => (
        item.id === updated.id ? updated : item
      )));
      setStatusMessage("admin.orders.shipmentUpdated");
    } catch (error) {
      setStatusMessage("admin.orders.saveFailed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="admin-orders">
      <Seo
        title={t("admin.orders.seoTitle")}
        description={t("admin.orders.seoDescription")}
        path="/admin/orders"
        noindex
      />
      <header className="admin-orders-header">
        <div>
          <h1>{t("admin.orders.title")}</h1>
          <nav>
            <Link to="/admin">{t("admin.nav.products")}</Link>
            <Link to="/admin/users">{t("admin.nav.users")}</Link>
            <Link to="/admin/custom-requests">{t("admin.nav.requests")}</Link>
            <Link to="/admin/custom-projects">{t("admin.nav.projects")}</Link>
          </nav>
        </div>
        {auth ? <button type="button" onClick={handleLogout}>{t("admin.logout")}</button> : null}
      </header>

      {!auth && (
        <form className="admin-orders-login" onSubmit={handleLogin}>
          <h2>{t("admin.loginTitle")}</h2>
          <label>
            {t("admin.username")}
            <input
              type="text"
              value={login.username}
              onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))}
            />
          </label>
          <label>
            {t("admin.password")}
            <input
              type="password"
              value={login.password}
              onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))}
            />
          </label>
          <button type="submit">{t("admin.login")}</button>
        </form>
      )}

      {auth && (
        <section className="admin-orders-layout">
          <aside className="admin-orders-list">
            <div className="admin-orders-search">
              <input
                type="search"
                value={search}
                placeholder={t("admin.orders.searchPlaceholder")}
                aria-label={t("admin.orders.search")}
                onChange={(event) => setSearch(event.target.value)}
              />
              <button type="button" onClick={fetchOrders} disabled={loading}>
                {t(loading ? "admin.loading" : "admin.orders.search")}
              </button>
            </div>
            {orders.length ? (
              orders.map((order) => (
                <button
                  type="button"
                  className={`admin-order-list-item${selectedOrder?.id === order.id ? " selected" : ""}`}
                  key={order.id}
                  onClick={() => setSelectedId(order.id)}
                >
                  <strong>{order.id}</strong>
                  <span>{order.email}</span>
                  <em>{shipmentLabel(order.shipmentStatus)}</em>
                  <small>{orderDate(order.paidAt || order.createdAt)}</small>
                  {order.trackingNumber ? <small>{order.trackingNumber}</small> : null}
                </button>
              ))
            ) : (
              <p className="admin-orders-empty">{t("admin.orders.empty")}</p>
            )}
          </aside>

          <section className="admin-order-detail">
            {selectedOrder ? (
              <>
                <div className="admin-order-heading">
                  <div>
                    <p>{t("admin.orders.orderId")}</p>
                    <h2>{selectedOrder.id}</h2>
                  </div>
                  <span>{orderStatusLabel(selectedOrder.status)}</span>
                </div>

                <div className="admin-order-fields">
                  <div>
                    <span>{t("admin.orders.email")}</span>
                    <a href={`mailto:${selectedOrder.email}`}>{selectedOrder.email}</a>
                  </div>
                  <div>
                    <span>{t("admin.orders.customerName")}</span>
                    <strong>{selectedOrder.customerName || t("admin.notSet")}</strong>
                  </div>
                  <div>
                    <span>{t("admin.orders.phone")}</span>
                    <strong>{selectedOrder.customerPhone || t("admin.notSet")}</strong>
                  </div>
                  <div>
                    <span>{t("admin.orders.country")}</span>
                    <strong>{selectedOrder.countryCode ? formatRegion(selectedOrder.countryCode) : t("admin.notSet")}</strong>
                  </div>
                  <div>
                    <span>{t("admin.orders.stripeSession")}</span>
                    <strong>{selectedOrder.stripeSessionId || t("admin.notSet")}</strong>
                  </div>
                  <div>
                    <span>{t("admin.orders.paidAt")}</span>
                    <strong>{orderDate(selectedOrder.paidAt)}</strong>
                  </div>
                </div>

                <div className="admin-shipment-editor">
                  <h3>{t("admin.orders.shipmentTracking")}</h3>
                  <label>
                    {t("admin.status")}
                    <select
                      value={shipmentDraft.shipmentStatus}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, shipmentStatus: event.target.value }))}
                    >
                      {SHIPMENT_STATUSES.map((status) => (
                        <option value={status} key={status}>{shipmentLabel(status)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {t("admin.orders.carrier")}
                    <input
                      type="text"
                      value={shipmentDraft.trackingCarrier}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, trackingCarrier: event.target.value }))}
                    />
                  </label>
                  <label>
                    {t("admin.orders.trackingNumber")}
                    <input
                      type="text"
                      value={shipmentDraft.trackingNumber}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, trackingNumber: event.target.value }))}
                    />
                  </label>
                  <label>
                    {t("admin.orders.trackingUrl")}
                    <input
                      type="url"
                      value={shipmentDraft.trackingUrl}
                      placeholder="https://www.yanwenexpress.com/tracking.html"
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, trackingUrl: event.target.value }))}
                    />
                  </label>
                  <label className="admin-shipment-note">
                    {t("admin.orders.shippingNote")}
                    <textarea
                      rows="4"
                      value={shipmentDraft.adminShippingNote}
                      onChange={(event) => setShipmentDraft((current) => ({ ...current, adminShippingNote: event.target.value }))}
                    />
                  </label>
                  <button type="button" onClick={saveShipment} disabled={saving}>
                    {t(saving ? "admin.saving" : "admin.orders.saveTracking")}
                  </button>
                </div>

                <div className="admin-order-items">
                  <h3>{t("admin.orders.items")}</h3>
                  {(selectedOrder.items || []).map((item) => (
                    <article className="admin-order-item" key={item.itemKey || `${selectedOrder.id}-${item.id}`}>
                      {itemImage(item) ? (
                        <img src={resolveImageUrl(itemImage(item))} alt={item.name} />
                      ) : null}
                      <div>
                        <strong>{item.name}</strong>
                        <span>{item.customColorName || item.selectedColor || item.color || t("admin.orders.defaultColor")}</span>
                        <span>{t("admin.orders.itemQuantity", { quantity: formatNumber(Number(item.quantity || 0)) })}</span>
                        <span>{t("admin.orders.itemPrice", { price: formatNumber(Number(item.price || 0), { style: "currency", currency: "USD" }) })}</span>
                      </div>
                      {item.type === "custom_yoyo" ? (
                        <div className="admin-custom-item-media">
                          {item.artworkUrl ? <a href={resolveImageUrl(item.artworkUrl)} target="_blank" rel="noreferrer">{t("admin.orders.originalArtwork")}</a> : null}
                          {item.previewImageUrl ? <a href={resolveImageUrl(item.previewImageUrl)} target="_blank" rel="noreferrer">{t("admin.orders.adjustedPreview")}</a> : null}
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <p className="admin-orders-empty">{t("admin.orders.selectOrder")}</p>
            )}
          </section>
        </section>
      )}

      {statusMessage && <p className="admin-orders-status" role="status">{t(statusMessage)}</p>}
    </main>
  );
};

export default AdminOrders;
